import React, { useState, useMemo, Suspense } from 'react'

import * as THREE from 'three'

import { Canvas } from '@react-three/fiber'
import { OrbitControls, SpotLight, Environment } from '@react-three/drei'

// Models
import ButtonsTG60, { BUTTONS_FLUSH_DIMENSIONS } from './models/Buttons_TG60'
import DialHUV1 from './models/Dial_HUV1'
import SliderPocketLighter from './models/Slider_Pocket_Lighter'
import SliderTP1 from './models/Slider_TP1'

export const HOVER_COLOR = '#fff'
export const PLANE_COLOR = '#DBDBDB'
export const MATERIAL_COLOR = '#DBDBDB'

const Models = ({ enableSelection, onSelect }) => {
	const [isInteractingWithModel, setIsInteractingWithModel] = useState(false)
	const [selectedModel, setSelectedModel] = useState(null)
	const size = 64

	const handleSelect = (model) => {
		if (!enableSelection) return
		onSelect(model)
		setSelectedModel(model)
	}

	return (
		<>
			{/* <ambientLight intensity={0.8} /> */}

			<SpotLight
				castShadow
				distance={0}
				position={[20, 25, 10]}
				shadowMapHeight={4096}
				shadowMapWidth={4096}
				penumbra={1}
				angle={0.15}
				attenuation={5}
				anglePower={5}
			/>

			<Suspense fallback={null}>
				<OrbitControls
					enablePan={false}
					enableZoom={true}
					enableRotate={!isInteractingWithModel}
					minAzimuthAngle={130 * (Math.PI / 180)}
					maxAzimuthAngle={190 * (Math.PI / 180)}
					minPolarAngle={0 * (Math.PI / 180)}
					maxPolarAngle={45 * (Math.PI / 180)}
					minZoom={window.innerWidth > 1024 ? 274 : 110}
					maxZoom={window.innerWidth > 1024 ? 340 : 150}
					rotateSpeed={0.15}
					enableDamping
					target={[0.5, 0, -0.5]}
				/>

				<ButtonsTG60
					enableSelection={enableSelection}
					position={[
						-BUTTONS_FLUSH_DIMENSIONS.length / 2,
						0,
						BUTTONS_FLUSH_DIMENSIONS.width / 2
					]}
					onSelect={() => handleSelect('buttons_tg60')}
					isSelected={selectedModel === 'buttons_tg60'}
				/>
				<DialHUV1
					enableSelection={enableSelection}
					position={[0.82, 0, 0.5]}
					onUseDrag={(state) => setIsInteractingWithModel(state)}
					onSelect={() => handleSelect('dial_huv1')}
					isSelected={selectedModel === 'dial_huv1'}
				/>
				<SliderPocketLighter
					enableSelection={enableSelection}
					position={[1.32, 0, -1]}
					rotation={[0, Math.PI / 1, 0]}
					onUseDrag={(state) => setIsInteractingWithModel(state)}
					onSelect={() => handleSelect('slider_pocket_lighter')}
					isSelected={selectedModel === 'slider_pocket_lighter'}
				/>
				<SliderTP1
					enableSelection={enableSelection}
					position={[0, 0, -1]}
					rotation={[0, Math.PI / 1, 0]}
					onUseDrag={(state) => setIsInteractingWithModel(state)}
					onSelect={() => handleSelect('slider_tp1')}
					isSelected={selectedModel === 'slider_tp1'}
				/>
				<Environment preset={'apartment'} />
				<Background
					size={size}
					rotation={[-Math.PI / 2, 0, Math.PI / 1]}
					position={[size / 2, 0.3175, -size / 2]}
					cutout={[
						[30.18, 30.5],
						[30.18, 32.5],
						[32.82, 32.5],
						[32.82, 30.5]
					]}
					onClick={() => handleSelect(null)}
				/>
			</Suspense>
		</>
	)
}

const Background = ({
	start = [0, 0],
	size = 1,
	cutout = [
		[0, 0],
		[0, 0],
		[0, 0],
		[0, 0],
		[0, 0]
	],
	...props
}) => {
	const shape = useMemo(() => {
		const shape = new THREE.Shape()
		shape.moveTo(...start)
		shape.lineTo(0, size)
		shape.lineTo(size, size)
		shape.lineTo(size, 0)
		shape.lineTo(0, 0)

		const hole = new THREE.Shape()
		hole.moveTo(cutout[0][0], cutout[0][1])
		hole.lineTo(cutout[1][0], cutout[1][1])
		hole.lineTo(cutout[2][0], cutout[2][1])
		hole.lineTo(cutout[3][0], cutout[3][1])

		shape.holes.push(hole)
		return shape
	}, [start, size, cutout])

	return (
		<mesh {...props} receiveShadow>
			<shapeGeometry args={[shape]} />
			<meshStandardMaterial color={PLANE_COLOR} />
		</mesh>
	)
}

const Scene = ({ onSelect }) => {
	const enableSelection = false

	return (
		<>
			<Canvas
				camera={{
					position: [3, 6, -4],
					zoom: window.innerWidth > 1024 ? 274 : 120
				}}
				orthographic
				shadows>
				<Models
					enableSelection={enableSelection}
					onSelect={(model) => onSelect(model)}
				/>
			</Canvas>
		</>
	)
}

export default Scene
